import React, { FC, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { I18nextProvider } from 'react-i18next'
import { QueryClientProvider } from 'react-query'
import { Provider } from 'react-redux'
import i18next from 'i18next'
import { v4 as UUID } from 'uuid'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AlertSnackbar } from '../components/Common/AlertSnackbar'
import { MainButton } from '../components/Common/MainButton'
import { ThemeContextProvider } from '../components/Context/ThemeContextProvider'
import { GenericDialog } from '../components/GenericDialog'
import { VesselQueryClient } from '../query-config'
import { store } from '../store/store'
import { ShowDialogParams } from '../types/util-types'

type Color = 'success' | 'info' | 'warning' | 'error'

export function showMessage(msg: string, severity: Color = 'success') {
  const formatted = typeof msg === 'string' ? msg : JSON.stringify(msg)
  const key = `${UUID()}`
  const rootel = document.createElement('div')
  rootel.id = key
  const root = createRoot(rootel)

  root.render(<Message msg={formatted} severity={severity} root={root} />)
}

interface MessageProps {
  msg: string
  severity?: Color
  root?: HTMLDivElement
}

const Message = ({ msg, severity, root }: MessageProps) => {
  const [open, setOpen] = useState(true)

  const onClose = () => {
    setOpen(false)
    setTimeout(() => {
      // @ts-ignore
      root.unmount()
    }, 100)
  }

  return <AlertSnackbar open={open} msg={msg} onClose={onClose} severity={severity} />
}

export interface ShowDialogProps<T = any> {
  pop: (...args: any[]) => T
}

export function showDialog<T = any, K = any>(
  Content: FC<K & ShowDialogProps<T>>,
  options: ShowDialogParams<K> = {},
): Promise<T | undefined> {
  const { opts, componentProps, scroll } = options
  const key = `${UUID()}`
  const rootel = document.createElement('div')
  rootel.id = key
  const root = createRoot(rootel)

  return new Promise<any>(resolve => {
    // @ts-ignore
    root.render(
      <DialogWrapper
        pop={resolve}
        scroll={scroll}
        content={Content}
        opts={opts}
        contentProps={componentProps}
        root={root}
      />,
    )
  })
}

const DialogWrapper = ({
  content: Content,
  pop,
  scroll = undefined,
  contentProps = {},
  opts = {},
  root = undefined,
}) => {
  const { fullWidth = true, maxWidth = 'md', disableBackdropClick = false } = opts as any
  const theme = useTheme()
  const isSmall = useMediaQuery<any>(theme.breakpoints.down('md'))

  const onPop = (data?: any) => {
    root.unmount()

    return pop(data)
  }

  return (
    <QueryClientProvider client={VesselQueryClient}>
      <ThemeContextProvider>
        <I18nextProvider i18n={i18next}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {/*@ts-ignore*/}
            <Provider store={store}>
              <Dialog
                // style={{ zIndex: 1000000 }} // TODO: Figure out why this was here
                // that cant be there as it will break the datepickers in the dialog
                scroll={scroll}
                keepMounted={false}
                open={true}
                onClose={(event, reason) => {
                  if (
                    (disableBackdropClick && reason !== 'backdropClick' && reason !== 'escapeKeyDown') ||
                    !disableBackdropClick
                  ) {
                    onPop() // TODO: test this
                  }
                }}
                fullWidth={fullWidth}
                fullScreen={isSmall}
                className="scrollbar"
                maxWidth={maxWidth}
                // disableBackdropClick={disableBackdropClick}
              >
                <Content {...contentProps} pop={onPop} />
              </Dialog>
            </Provider>
          </LocalizationProvider>
        </I18nextProvider>
      </ThemeContextProvider>
    </QueryClientProvider>
  )
}

export const DialogContainer = ({
  content: Content,
  pop,
  contentProps = {},
  opts = {},
  isAutoCloseDisabled = false,
  title,
  keyIndex = null,
  customComponentInTitle = null,
  fullScreen = false,
}) => {
  const {
    maxWidth = 'md',
    fullWidth,
    container,
    stableGutters = true,
    disableBackdropClick = false,
    disableEscClose = true,
  } = opts as any

  const [titleActionData, setTitleActionData] = React.useState(null)
  const onPop = (data?: any) => {
    return pop(data)
  }

  const renderCustomComponent = () => {
    if (!customComponentInTitle) return null

    if (typeof customComponentInTitle === 'function') {
      return customComponentInTitle({ onDataChange: setTitleActionData, pop: onPop, titleActionData })
    }

    if (React.isValidElement(customComponentInTitle)) {
      return React.cloneElement(customComponentInTitle, {
        onDataChange: setTitleActionData,
        pop: onPop,
        titleActionData,
        ...((customComponentInTitle.props as any) || {}),
      })
    }

    return customComponentInTitle
  }

  return (
    <GenericDialog
      open
      disableAutoClose={isAutoCloseDisabled || disableBackdropClick}
      disableEscClose={disableEscClose}
      onClose={onPop}
      maxWidth={maxWidth}
      title={title}
      key={keyIndex}
      fullWidth={fullWidth}
      removedCloseButton={true}
      container={container}
      stableGutters={stableGutters}
      customComponentInTitle={renderCustomComponent()}
      fullScreen={fullScreen}
    >
      <Content
        {...contentProps}
        pop={onPop}
        titleActionData={titleActionData}
        setTitleActionData={setTitleActionData}
      />
    </GenericDialog>
  )
}

export const AlertDialog = ({
  title = null,
  text,
  acceptText = 'Accept',
  cancelText = 'Cancel',
  pop,
  onConfirm = null,
}) => {
  return (
    <>
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent>
        {text && <DialogContentText>{text}</DialogContentText>}
        <DialogActions>
          <MainButton
            variant="contained"
            onClick={() => {
              if (onConfirm) onConfirm()
              pop(true)
            }}
            data-cy="deleteConfirm"
          >
            {acceptText}
          </MainButton>
          <MainButton variant="text" onClick={() => pop(false)}>
            {cancelText}
          </MainButton>
        </DialogActions>
      </DialogContent>
    </>
  )
}

export function showConfirmDialog(
  title: string,
  text?: string,
  { acceptText = undefined, cancelText = undefined } = {},
) {
  const key = `${UUID()}`
  const rootel = document.createElement('div')
  rootel.id = key
  const root = createRoot(rootel)
  return new Promise<boolean>(resolve => {
    root.render(
      <DialogWrapper
        pop={resolve}
        opts={{ fullWidth: false }}
        content={AlertDialog}
        contentProps={{ title, text, acceptText, cancelText }}
        root={root}
      />,
    )
  })
}
